/**
 * Brand kit: how a product sounds, what it never says, and the extra facts a manager has confirmed.
 *
 * The team's fixed rules (adsProducts.ts) always apply and cannot be switched off here. A manager can only add to them:
 * a voice note, phrases that must never appear (with what to say instead), and facts we may state. Pure, so the CMS
 * and the AI Worker agree on what is flagged.
 */
import { z } from "zod";
import { AD_PRODUCTS, adProductIdSchema, type AdProductId } from "./adsProducts.ts";

export const BRAND_LIMITS = { voice: 800, rules: 30, phrase: 80, sayInstead: 200, facts: 30, fact: 200 } as const;

const text = (max: number) => z.string().trim().max(max);

export const neverSayRuleSchema = z.object({
  phrase: text(BRAND_LIMITS.phrase).min(3, "A phrase needs at least 3 letters"),
  sayInstead: text(BRAND_LIMITS.sayInstead).default(""),
});
export type NeverSayRule = z.infer<typeof neverSayRuleSchema>;

export const brandKitSchema = z.object({
  voice: text(BRAND_LIMITS.voice).default(""),
  neverSay: z.array(neverSayRuleSchema).max(BRAND_LIMITS.rules).default([]),
  facts: z.array(text(BRAND_LIMITS.fact).min(3)).max(BRAND_LIMITS.facts).default([]),
  /** Where a fact comes from: the fact's text -> a web address. A fact may have none. Only https addresses are kept (v92). */
  factSources: z.record(text(BRAND_LIMITS.fact), z.string().trim().max(1000)).default({}),
});
export type BrandKit = z.infer<typeof brandKitSchema>;
export const EMPTY_BRAND_KIT: BrandKit = { voice: "", neverSay: [], facts: [], factSources: {} };

/** Keeps a source only for a fact that is still there, and only when it is an http(s) address. */
export function cleanFactSources(facts: readonly string[], sources: Record<string, string> | null | undefined): Record<string, string> {
  const out: Record<string, string> = {};
  for (const f of facts) {
    const raw = (sources?.[f] ?? "").trim();
    if (/^https?:\/\/[^\s]+$/i.test(raw)) out[f] = raw.slice(0, 1000);
  }
  return out;
}

export const IMAGE_STYLE_MAX = 1500;
export const IMAGE_GUARDRAIL_LIMITS = { custom: 20, line: 200 } as const;

/**
 * What the AI scene may show. `allowAiScene` off means no AI picture at all: the ad keeps the flat template.
 * `people` off bans people, faces and hands. `scene` on allows a setting (a room, a desk); off asks for a single object
 * on a plain surface. `custom` lines are added to the picture prompt word for word.
 */
export const imageGuardrailsSchema = z.object({
  allowAiScene: z.boolean().default(true),
  people: z.boolean().default(false),
  scene: z.boolean().default(true),
  custom: z.array(text(IMAGE_GUARDRAIL_LIMITS.line).min(3)).max(IMAGE_GUARDRAIL_LIMITS.custom).default([]),
});
export type ImageGuardrails = z.infer<typeof imageGuardrailsSchema>;
export const DEFAULT_IMAGE_GUARDRAILS: ImageGuardrails = { allowAiScene: true, people: false, scene: true, custom: [] };

/** Used until a manager writes their own. */
export const AD_IMAGE_STYLE_DEFAULTS: Record<AdProductId, string> = {
  sonic_hire: "Clean product-UI feel, navy or light background, brand blue highlight, one supporting visual at most.",
  raya: "Calm clinic setting, soft neutrals, brand accent, one supporting visual at most.",
};

export const adImageKitSchema = z.object({ style: text(IMAGE_STYLE_MAX).default(""), guardrails: imageGuardrailsSchema.default(DEFAULT_IMAGE_GUARDRAILS) });
export type AdImageKit = z.infer<typeof adImageKitSchema>;

/** The picture settings read from storage. Bad or missing parts become the defaults. */
export function readImageKit(product: AdProductId, style: string | null | undefined, guardrails: string | null | undefined): AdImageKit & { isDefaultStyle: boolean } {
  let raw: unknown;
  try {
    raw = JSON.parse(guardrails || "{}");
  } catch {
    raw = {};
  }
  const parsed = imageGuardrailsSchema.safeParse(raw);
  const g = parsed.success ? parsed.data : { ...DEFAULT_IMAGE_GUARDRAILS, ...(typeof raw === "object" && raw ? imageGuardrailsSchema.partial().catch({}).parse(raw) : {}) };
  const own = (style ?? "").trim().slice(0, IMAGE_STYLE_MAX);
  return { style: own || AD_IMAGE_STYLE_DEFAULTS[product], guardrails: imageGuardrailsSchema.parse(g), isDefaultStyle: own === "" };
}

export const brandKitSaveSchema = z.object({ siteId: z.string().min(1).max(100), product: adProductIdSchema, kit: brandKitSchema, /** Left out: the picture settings stay as they are. */ image: adImageKitSchema.optional() });

/** What the Brand kit screen shows: the manager's additions, and the fixed team rules that always apply. */
export const brandKitViewSchema = z.object({
  product: adProductIdSchema,
  kit: brandKitSchema,
  /** The fixed rules from the team, in plain words. Read only. */
  builtIn: z.object({
    rules: z.array(z.string()),
    facts: z.array(z.string()),
    /** Names, logos and quotes that need written approval before they can appear. */
    needsApproval: z.array(z.string()),
  }),
  /** Picture style and guardrails for the AI scene behind LinkedIn ads. `isDefaultStyle` is true while the style is the built-in one. */
  image: adImageKitSchema.extend({ isDefaultStyle: z.boolean() }).default({ style: "", guardrails: DEFAULT_IMAGE_GUARDRAILS, isDefaultStyle: true }),
  updatedAt: z.string().nullable(),
  updatedByName: z.string().nullable(),
});
export type BrandKitView = z.infer<typeof brandKitViewSchema>;

export function builtInBrand(product: AdProductId) {
  const p = AD_PRODUCTS[product];
  return { rules: [...p.messagingRules], facts: [...p.facts], needsApproval: [...new Set([p.mainCompetitor.name, ...p.protectedNames])] };
}

/** A kit read from storage: bad or missing pieces become empty rather than breaking the page. */
export function readBrandKit(voice: string | null | undefined, neverSay: string | null | undefined, facts: string | null | undefined, factSources?: string | null): BrandKit {
  const json = (s: string | null | undefined): unknown => {
    try {
      return JSON.parse(s || "[]");
    } catch {
      return [];
    }
  };
  const rules = z.array(z.unknown()).catch([]).parse(json(neverSay)).flatMap((r) => {
    const p = neverSayRuleSchema.safeParse(r);
    return p.success ? [p.data] : [];
  });
  const fs = z.array(z.unknown()).catch([]).parse(json(facts)).flatMap((f) => {
    const p = text(BRAND_LIMITS.fact).min(3).safeParse(f);
    return p.success ? [p.data] : [];
  });
  const kept = fs.slice(0, BRAND_LIMITS.facts);
  let raw: unknown;
  try {
    raw = JSON.parse(factSources || "{}");
  } catch {
    raw = {};
  }
  const rec = raw && typeof raw === "object" && !Array.isArray(raw) ? (raw as Record<string, string>) : {};
  return { voice: (voice ?? "").slice(0, BRAND_LIMITS.voice), neverSay: rules.slice(0, BRAND_LIMITS.rules), facts: kept, factSources: cleanFactSources(kept, rec) };
}

/** The same phrase written twice (any capitals, any spacing) counts once. Keeps the first. */
export function dedupeRules(rules: NeverSayRule[]): NeverSayRule[] {
  const seen = new Set<string>();
  return rules.filter((r) => {
    const k = r.phrase.toLowerCase().replace(/\s+/g, " ");
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
}

const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/** The rules whose phrase appears in the text. A phrase matches whole words, in any capitals, with any spacing between words. */
export function brandRulesBroken(text: string, rules: readonly NeverSayRule[]): NeverSayRule[] {
  return rules.filter((r) => {
    const words = r.phrase.trim().split(/\s+/).map(escapeRe).join("\\s+");
    return words.length > 0 && new RegExp(`(^|[^\\p{L}\\p{N}])${words}(?![\\p{L}\\p{N}])`, "iu").test(text);
  });
}

/** The sentence a reviewer reads when a draft breaks one of the kit's rules. */
export function brandProblemText(rule: NeverSayRule): string {
  return rule.sayInstead ? `Brand kit: never say “${rule.phrase}”. ${rule.sayInstead}` : `Brand kit: never say “${rule.phrase}”.`;
}

/** What the models are told, so the kit changes what is written and not only what is flagged afterwards. Empty when the kit is empty. */
export function renderBrandKitForModel(kit: BrandKit): string {
  const lines: string[] = [];
  if (kit.voice) lines.push(`VOICE: ${kit.voice}`);
  if (kit.neverSay.length) lines.push("NEVER SAY (a draft that says one of these is thrown away):", ...kit.neverSay.map((r) => `- “${r.phrase}”${r.sayInstead ? `. Instead: ${r.sayInstead}` : ""}`));
  if (kit.facts.length) lines.push("EXTRA FACTS YOU MAY STATE (confirmed by a manager):", ...kit.facts.map((f) => `- ${f}`));
  return lines.join("\n");
}
